import React, { ChangeEvent, useState } from "react";

import Button from "../../../components/ui/Button";
import { Checkbox } from "../../../components/ui/Checkbox";
import Select from "../../../components/ui/Select";

interface BulkActionsModalProps {
	isOpen: boolean;
	onClose: () => void;
	selectedLeases: string[];
	onBulkAction: (action: BulkAction) => void;
}

export interface BulkAction {
	type: string;
	data: ActionData;
	leaseIds: string[];
}

interface ActionData {
	duration?: string | number;
	rentIncrease?: string;
	notificationType?: string;
	customMessage?: string;
	documentType?: string;
	emailToTenants?: boolean;
	increaseType?: string;
	increaseValue?: string;
	effectiveDate?: string;
	extensionPeriod?: string;
	maintainRent?: boolean;
}

const BulkActionsModal: React.FC<BulkActionsModalProps> = ({
	isOpen,
	onClose,
	selectedLeases,
	onBulkAction,
}) => {
	const [actionType, setActionType] = useState("");
	const [actionData, setActionData] = useState<ActionData>({});
	const [confirmAction, setConfirmAction] = useState(false);

	const actionOptions = [
		{ value: "export", label: "Export to CSV" },
		{ value: "renew", label: "Bulk Renewal Processing" },
		{ value: "notify", label: "Send Notifications" },
		{ value: "generate-docs", label: "Generate Documents" },
		{ value: "update-rent", label: "Update Rent Amounts" },
		{ value: "extend-lease", label: "Extend Lease Terms" },
	];

	const notificationTypeOptions = [
		{ value: "renewal-reminder", label: "Lease Renewal Reminder" },
		{ value: "expiration-notice", label: "Lease Expiration Notice" },
		{ value: "rent-increase", label: "Rent Increase Notice" },
		{ value: "general-notice", label: "General Notice" },
	];

	const documentTypeOptions = [
		{ value: "lease-agreement", label: "Lease Agreement" },
		{ value: "renewal-notice", label: "Renewal Notice" },
		{ value: "termination-notice", label: "Termination Notice" },
		{ value: "rent-receipt", label: "Rent Receipt" },
	];

	const handleActionChange = (value: string) => {
		setActionType(value);
		setActionData({});
		setConfirmAction(false);
	};

	const handleSubmit = () => {
		if (!confirmAction) return;

		onBulkAction({
			type: actionType,
			data: actionData,
			leaseIds: selectedLeases,
		});

		onClose();
		setActionType("");
		setActionData({});
		setConfirmAction(false);
	};

	const renderActionForm = () => {
		switch (actionType) {
			case "renew":
				return (
					<div className="space-y-4">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									New Lease Duration (months)
								</label>
								<input
									type="number"
									className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
									placeholder="12"
									value={actionData.duration || ""}
									onChange={(e: ChangeEvent<HTMLInputElement>) =>
										setActionData({
											...actionData,
											duration: String(e.target.value),
										})
									}
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Rent Increase (%)
								</label>
								<input
									type="number"
									className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
									placeholder="5"
									value={actionData.rentIncrease || ""}
									onChange={(e: ChangeEvent<HTMLInputElement>) =>
										setActionData({
											...actionData,
											rentIncrease: e.target.value,
										})
									}
								/>
							</div>
						</div>
					</div>
				);

			case "notify":
				return (
					<div className="space-y-4">
						<Select
							label="Notification Type"
							options={notificationTypeOptions}
							value={actionData.notificationType || ""}
							onValueChange={(value) =>
								setActionData({
									...actionData,
									notificationType: String(value),
								})
							}
							required
						/>
						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								Custom Message (Optional)
							</label>
							<textarea
								className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
								rows={4}
								placeholder="Enter custom message..."
								value={actionData.customMessage || ""}
								onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
									setActionData({
										...actionData,
										customMessage: e.target.value,
									})
								}
							/>
						</div>
					</div>
				);

			case "generate-docs":
				return (
					<div className="space-y-4">
						<Select
							label="Document Type"
							options={documentTypeOptions}
							value={actionData.documentType || ""}
							onValueChange={(value) =>
								setActionData({ ...actionData, documentType: String(value) })
							}
							required
						/>
						<div className="flex items-center space-x-2">
							<Checkbox
								checked={actionData.emailToTenants || false}
								onChange={(e: ChangeEvent<HTMLInputElement>) =>
									setActionData({
										...actionData,
										emailToTenants: e.target.checked,
									})
								}
							/>
							<label className="text-sm text-foreground">
								Email documents to tenants automatically
							</label>
						</div>
					</div>
				);

			case "update-rent":
				return (
					<div className="space-y-4">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									Increase Type
								</label>
								<select
									className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
									value={actionData.increaseType || ""}
									onChange={(e: ChangeEvent<HTMLSelectElement>) =>
										setActionData({
											...actionData,
											increaseType: e.target.value,
										})
									}
								>
									<option value="">Select type</option>
									<option value="percentage">Percentage</option>
									<option value="fixed">Fixed Amount</option>
								</select>
							</div>
							<div>
								<label className="block text-sm font-medium text-foreground mb-2">
									{actionData.increaseType === "percentage"
										? "Percentage (%)"
										: "Amount ($)"}
								</label>
								<input
									type="number"
									className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
									placeholder={
										actionData.increaseType === "percentage" ? "5" : "100"
									}
									value={actionData.increaseValue || ""}
									onChange={(e: ChangeEvent<HTMLInputElement>) =>
										setActionData({
											...actionData,
											increaseValue: e.target.value,
										})
									}
								/>
							</div>
						</div>
						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								Effective Date
							</label>
							<input
								type="date"
								className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
								value={actionData.effectiveDate || ""}
								onChange={(e: ChangeEvent<HTMLInputElement>) =>
									setActionData({
										...actionData,
										effectiveDate: e.target.value,
									})
								}
							/>
						</div>
					</div>
				);

			case "extend-lease":
				return (
					<div className="space-y-4">
						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								Extension Period (months)
							</label>
							<input
								type="number"
								className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
								placeholder="6"
								value={actionData.extensionPeriod || ""}
								onChange={(e: ChangeEvent<HTMLInputElement>) =>
									setActionData({
										...actionData,
										extensionPeriod: e.target.value,
									})
								}
							/>
						</div>
						<div className="flex items-center space-x-2">
							<Checkbox
								checked={actionData.maintainRent || false}
								onChange={(e: ChangeEvent<HTMLInputElement>) =>
									setActionData({
										...actionData,
										maintainRent: e.target.checked,
									})
								}
							/>
							<label className="text-sm text-foreground">
								Maintain current rent amount
							</label>
						</div>
					</div>
				);

			default:
				return null;
		}
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
			<div className="bg-card border border-border rounded-lg w-full max-w-lg modal-shadow">
				{/* Header */}
				<div className="p-6 border-b border-border">
					<div className="flex items-center justify-between">
						<div>
							<h2 className="text-xl font-semibold text-foreground">
								Bulk Actions
							</h2>
							<p className="text-sm text-muted-foreground mt-1">
								{selectedLeases.length} lease
								{selectedLeases.length !== 1 ? "s" : ""} selected
							</p>
						</div>
						<Button
							variant="ghost"
							size="icon"
							onClick={onClose}
							iconName="X"
						/>
					</div>
				</div>

				{/* Content */}
				<div className="p-6 space-y-6">
					<Select
						label="Select Action"
						options={actionOptions}
						value={actionType}
						onValueChange={(value) => handleActionChange(String(value))}
						placeholder="Choose an action to perform"
						required
					/>

					{actionType && (
						<>
							{renderActionForm()}

							<div className="bg-muted/50 p-4 rounded-lg">
								<div className="flex items-start space-x-3">
									<Checkbox
										checked={confirmAction}
										onChange={(e: ChangeEvent<HTMLInputElement>) =>
											setConfirmAction(e.target.checked)
										}
									/>
									<div>
										<p className="text-sm font-medium text-foreground">
											Confirm Bulk Action
										</p>
										<p className="text-xs text-muted-foreground mt-1">
											I understand that this action will be applied to{" "}
											{selectedLeases.length} selected lease
											{selectedLeases.length !== 1 ? "s" : ""} and cannot be
											undone.
										</p>
									</div>
								</div>
							</div>
						</>
					)}
				</div>

				{/* Footer */}
				<div className="p-6 border-t border-border">
					<div className="flex items-center justify-end space-x-3">
						<Button variant="ghost" onClick={onClose}>
							Cancel
						</Button>
						<Button
							onClick={handleSubmit}
							disabled={!actionType || !confirmAction}
							iconName="Check"
							iconPosition="left"
						>
							Execute Action
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default BulkActionsModal;
